import { useState } from 'react';
import { faArrowUp, faInfoCircle } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { faSparkles } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Heading3, Paragraph } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { AI_PROMPT_MAX_LENGTH } from '../tabs/constants';

const SUGGESTIONS_SKELETON_COUNT = 3;
const SUGGESTIONS_SKELETON_KEYS = Array.from(
  { length: SUGGESTIONS_SKELETON_COUNT },
  (_, index) => `suggestion-skeleton-${index + 1}`,
);

interface CreateImageContentProps {
  suggestions?: string[];
  isLoadingSuggestions?: boolean;
  onSubmit: (prompt: string) => void | Promise<void>;
}

export function CreateImageContent({
  suggestions = [],
  isLoadingSuggestions = false,
  onSubmit,
}: CreateImageContentProps) {
  const [prompt, setPrompt] = useState('');

  const handlePromptSubmit = (rawPrompt: string) => {
    const trimmed = rawPrompt.trim();
    if (!trimmed) return;

    const capped = trimmed.slice(0, AI_PROMPT_MAX_LENGTH);
    onSubmit(capped);
    setPrompt('');
  };

  return (
    <div className="flex flex-col gap-4 p-4 pt-0">
      <div>
        <div className="flex items-center gap-2">
          <Heading3 className="text-base">AI Image Creator</Heading3>
          <Icon icon={faSparkles} className="text-primary" />
        </div>
        <div className="flex items-center gap-1.5">
          <Paragraph className="text-sm text-muted-foreground">
            Use AI to create custom images for your site
          </Paragraph>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                colorScheme="secondary"
                size="icon-sm"
                type="button"
                aria-label="More info"
                className="h-auto w-auto p-0 text-muted-foreground hover:text-foreground align-middle"
              >
                <Icon icon={faInfoCircle} className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-pretty">
              AI-generated images may contain inaccuracies or inconsistencies. Please review all
              generated content before publishing.
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      {isLoadingSuggestions ? (
        <div className="flex flex-wrap gap-2">
          {SUGGESTIONS_SKELETON_KEYS.map((key) => (
            <Skeleton key={key} className="h-8 w-32 rounded-full" />
          ))}
        </div>
      ) : suggestions.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {suggestions.map((s, index) => (
            <Button
              key={`${index}-${s}`}
              variant="outline"
              size="sm"
              type="button"
              className="rounded-full"
              onClick={() => onSubmit(s.slice(0, AI_PROMPT_MAX_LENGTH))}
            >
              {s}
            </Button>
          ))}
        </div>
      ) : null}

      <div>
        <Paragraph className="mb-1 text-sm font-medium">Or describe what you want to see</Paragraph>
        <div className="relative">
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. A professional law office with bookshelves and warm lighting"
            className="resize-none pr-10 text-sm h-32"
            rows={5}
            maxLength={AI_PROMPT_MAX_LENGTH}
          />
          <Button
            variant="ghost"
            size="sm"
            type="button"
            disabled={!prompt.trim()}
            onClick={() => {
              handlePromptSubmit(prompt);
            }}
            className={cn(
              'absolute bottom-3 right-2 h-8 w-8 p-0 border',
              prompt.trim()
                ? 'bg-background text-foreground border-border hover:bg-accent'
                : 'bg-muted text-muted-foreground border-border',
            )}
          >
            <Icon icon={faArrowUp} />
          </Button>
        </div>
      </div>
    </div>
  );
}
